import { useEffect, useRef, useState } from "react";
import { ControllersProvider, useControllers } from "./state/controllers";
import { useProjectStore } from "./state/projectStore";
import { usePlaybackStore } from "./state/playbackStore";
import { useEditorStore } from "./state/editorStore";
import { useEditor } from "./hooks/useEditor";
import { AnalysisPanel } from "./components/AnalysisPanel";
import { ScoreSection } from "./components/ScoreSection";
import { AboutSection } from "./components/AboutSection";
import { PlaybackControls } from "./components/PlaybackControls";
import { SongInput } from "./components/SongInput";
import { EditorDock } from "./components/EditorDock";
import { MyScores } from "./components/MyScores";
import { Logo } from "./components/Logo";
import { EditIcon } from "./components/icons";
import { useT } from "./i18n";
import { useAuth } from "./state/auth";
import { useUi } from "./state/ui";
import { timeForTick } from "./core/timeMap";
import { incompleteMeasures } from "./core/editor/validate";
import { userBeats } from "./core/notation/beatGrouping";
import type { NoteEvent } from "./types/domain";

function Shell() {
  const c = useControllers();
  const ed = useEditor();
  const sourceKind = useProjectStore((s) => s.source.kind);
  const hasChart = useProjectStore((s) => s.chartMeasures.length > 0);
  const editMode = useEditorStore((s) => s.editMode);
  const rangeModeUi = useEditorStore((s) => s.rangeMode);
  const scoreView = useUi((s) => s.scoreView);
  const scoresPage = useUi((s) => s.scoresPage);
  const setScoresPage = useUi((s) => s.setScoresPage);
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const scrollRef = useRef<HTMLElement>(null);
  const ptrType = useRef<string>("mouse");          // last pointer type (touch vs mouse)
  const startPos = useRef<{ x: number; y: number } | null>(null);   // press position (tap vs drag)
  const selAnchor = useRef<{ m: number; b: number } | null>(null);  // beat pressed in edit mode
  const rangeActivated = useRef(false);             // this gesture became a drag group-select

  // top-left logo → back to the initial analysis (input) screen
  const goHome = () => {
    c.song.pause(); c.youtube.stop(); c.score.stop();
    useEditorStore.getState().reset();
    usePlaybackStore.getState().setPlayMode("song");
    useProjectStore.getState().reset();
    setScoresPage(false);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  useEffect(() => {
    // no demo song — the app starts at the input screen until a file/YouTube link is analyzed
    c.cursorLoop.start();
    useAuth.getState().refresh();   // restore login session if any

    // editor supplies selection/modified-measure coloring to the score
    c.score.styleProvider = () => {
      const es = useEditorStore.getState();
      const ps = useProjectStore.getState();
      const notesByMeasure: Record<number, NoteEvent[]> = {};
      for (const k in ps.arrangement.measures) { const n = ps.arrangement.measures[+k].notes; if (n) notesByMeasure[+k] = n; }
      const capOf = (mi: number) => ps.arrangement.measures[mi]?.timeSignature?.[0]
        ?? ps.chartMeasures[mi]?.beats.length ?? 4;
      const incomplete = incompleteMeasures(notesByMeasure, capOf);
      let rangeByMeasure: Record<number, Set<number>> | undefined;
      if (es.rangeMode && es.rangeAnchor && es.rangeEnd) {
        const a = es.rangeAnchor, b = es.rangeEnd;
        const [lo, hi] = (a.m < b.m || (a.m === b.m && a.b <= b.b)) ? [a, b] : [b, a];
        const beatCount = (mi: number) => (notesByMeasure[mi] && notesByMeasure[mi].length)
          ? userBeats(notesByMeasure[mi]).length : (ps.chartMeasures[mi]?.beats.length ?? 4);
        rangeByMeasure = {};
        for (let mi = lo.m; mi <= hi.m; mi++) {
          const from = mi === lo.m ? lo.b : 0;
          const to = mi === hi.m ? hi.b : beatCount(mi) - 1;
          const set = new Set<number>();
          for (let bi = from; bi <= to; bi++) set.add(bi);
          rangeByMeasure[mi] = set;
        }
      }
      return { editMode: es.editMode, activeMeasure: es.activeMeasure, selectedNoteIdx: es.selectedNoteIdx, insertArmed: es.insertArmed, notesByMeasure, incomplete, rangeByMeasure };
    };

    // press a beat in edit mode: remember it; a DRAG becomes a group-select, a plain TAP selects (and
    // cancels any active group). Not editing → seek only while playing.
    c.score.onBeatClick = (info) => {
      const playing = c.activeSource().isPlaying() || c.score.playing;
      const es0 = useEditorStore.getState();
      if (es0.editMode && !playing) {
        selAnchor.current = { m: info.measureIndex, b: info.beatInBar };
        rangeActivated.current = false;
        return;
      }
      useEditorStore.getState().set({ pendingBeat: { m: info.measureIndex, b: info.beatInBar } });
      const measures = useProjectStore.getState().chartMeasures;
      c.score.setTickPosition(info.absolutePlaybackStart);   // move the playhead (both modes)
      if (usePlaybackStore.getState().playMode === "song" && measures.length) {
        const t = timeForTick(measures, info.absolutePlaybackStart);
        // playing → seek & keep going; stopped → move position silently (no YouTube auto-play)
        if (playing) c.activeSource().seek(t); else c.activeSource().seekSilent(t);
      }
    };
    const onPtrDown = (e: PointerEvent) => { ptrType.current = e.pointerType || "mouse"; startPos.current = { x: e.clientX, y: e.clientY }; };
    const onPtrMove = (e: PointerEvent) => {
      if (!selAnchor.current || !startPos.current) return;
      if (!rangeActivated.current) {
        const dx = e.clientX - startPos.current.x, dy = e.clientY - startPos.current.y;
        if (dx * dx + dy * dy < 64) return;                              // below the ~8px drag threshold
        if (ptrType.current !== "mouse" && Math.abs(dy) > Math.abs(dx)) { selAnchor.current = null; return; }  // touch vertical → let it scroll
        rangeActivated.current = true;                                   // a drag → activate group select
        ed.setRangeMode(true);
        ed.rangeStart(selAnchor.current.m, selAnchor.current.b);
      }
      const hit = c.score.beatAtClient(e.clientX, e.clientY);
      if (hit) ed.rangeExtend(hit.measureIndex, hit.beatInBar);
    };
    const onPtrUp = () => {
      if (selAnchor.current && !rangeActivated.current) {               // a plain tap (no drag)
        const a = selAnchor.current;
        if (useEditorStore.getState().rangeMode) ed.setRangeMode(false); // tap cancels the active group
        ed.selectMeasure(a.m, a.b);
      }
      selAnchor.current = null; rangeActivated.current = false;
    };
    const onPtrCancel = () => { selAnchor.current = null; rangeActivated.current = false; };
    window.addEventListener("pointerdown", onPtrDown, true);
    window.addEventListener("pointermove", onPtrMove, true);
    window.addEventListener("pointerup", onPtrUp);
    window.addEventListener("pointercancel", onPtrCancel);
    return () => {
      c.cursorLoop.stop(); c.score.onBeatClick = null; c.score.styleProvider = null;
      window.removeEventListener("pointerdown", onPtrDown, true);
      window.removeEventListener("pointermove", onPtrMove, true);
      window.removeEventListener("pointerup", onPtrUp); window.removeEventListener("pointercancel", onPtrCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={"app" + (editMode ? " editing" : "") + (rangeModeUi ? " ranging" : "")}>
      <header className={"topbar" + (scrolled ? " floating" : "")}>
        <button className="brand" onClick={goHome} aria-label={t("brand.home")}>
          <Logo />
          <span className="name"><em>Tutti</em></span>
          <span className="tagline">{t("brand.tagline")}</span>
        </button>
        <div className="topbar-spacer" />
        <PlaybackControls />
      </header>
      <main className="scroll-area" ref={scrollRef}
        onScroll={(e) => setScrolled((e.target as HTMLElement).scrollTop > 80)}>
        <div className="content">
          <SongInput />
          <AnalysisPanel />
          <section className={"section yt-wrap" + (sourceKind === "youtube" ? "" : " hidden")}>
            <h2>YouTube</h2>
            <div className="yt-frame"><div id="yt-host" /></div>
          </section>
          <ScoreSection />
          <AboutSection />
          <footer className="app-foot">
            <div className="foot-brand"><Logo size={18} /><span>Tutti</span></div>
            <nav className="foot-nav">
              <a href="/popular">{t("footer.popular")}</a>
              <a href="/about">{t("footer.about")}</a>
              <a href="/contact">{t("footer.contact")}</a>
              <a href="/terms">{t("footer.terms")}</a>
              <a href="/privacy">{t("footer.privacy")}</a>
            </nav>
            <div className="foot-copy">© 2026 Tutti · tutti-music.net</div>
          </footer>
        </div>
      </main>
      {editMode && <EditorDock />}
      {/* floating 편집 entry (타브악보 view) — the dock's ✓ handles exit, so hide while editing */}
      {hasChart && scoreView === "score" && !editMode && (
        <button className="edit-fab" onClick={() => ed.enterEditMode()} title={t("ctrl.edit")} aria-label={t("ctrl.edit")}>
          <EditIcon size={22} />
        </button>
      )}
      {scoresPage && <MyScores onClose={() => setScoresPage(false)} />}
    </div>
  );
}

export default function App() {
  return (
    <ControllersProvider>
      <Shell />
    </ControllersProvider>
  );
}
